// frontend/src/features/ups/modals/CreateSiteModal.js
// UPS Admin: Create Site modal
// - POST /ups-admin/sites
// - Validates code format client-side to match backend rules:
//   ^[A-Z0-9_-]{2,12}$

import React, { useMemo, useState } from 'react';
import axios from 'axios';

const CODE_REGEX = /^[A-Z0-9_-]{2,12}$/;

export default function CreateSiteModal({ isOpen, onClose, onCreated }) {
  const siteNameId = 'create-site-name';
  const siteCodeId = 'create-site-code';
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const normalized = useMemo(() => {
    return {
      name: (name ?? '').trim(),
      code: (code ?? '').trim().toUpperCase(),
    };
  }, [name, code]);

  if (!isOpen) return null;

  const validate = () => {
    if (!normalized.name) return 'Site name is required';
    if (!normalized.code) return 'Site code is required';
    if (!CODE_REGEX.test(normalized.code)) {
      return 'Code must be 2–12 chars: A–Z, 0–9, underscore, or dash (e.g. MAIN, DEPOT_1, SITE-2)';
    }
    return '';
  };

  const submit = async () => {
    setError('');
    const msg = validate();
    if (msg) return setError(msg);

    setSaving(true);
    try {
      const res = await axios.post('/ups-admin/sites', {
        name: normalized.name,
        code: normalized.code,
      });

      // Support either { site } or direct site
      const site = res.data?.site ?? res.data;
      onCreated?.(site);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to create site');
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    if (saving) return;
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={close}
    >
      <div
        className="bg-white rounded-lg w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-800">Create Site</h2>
          <button onClick={close} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded p-2">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label htmlFor={siteNameId} className="block text-sm text-gray-600 mb-1">Site name</label>
            <input
              id={siteNameId}
              name="siteName"
              className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Main Depot"
              disabled={saving}
            />
          </div>

          <div>
            <label htmlFor={siteCodeId} className="block text-sm text-gray-600 mb-1">Code</label>
            <input
              id={siteCodeId}
              name="siteCode"
              className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring font-mono"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="MAIN"
              disabled={saving}
            />
            <div className="text-xs text-gray-500 mt-1">
              Format: 2–12 chars, uppercase A–Z/0–9, underscore, dash. Example: <span className="font-mono">DEPOT_1</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={close}
            className="px-4 py-2 rounded border text-gray-700 hover:bg-gray-50 transition text-sm disabled:opacity-60"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition text-sm disabled:opacity-60"
            disabled={saving}
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
